import type {
  AssetCatalogFilterParams,
  AssetLocationUpdatedEvent,
  AssetStatusUpdatedEvent,
  CreateAssetCatalogDto,
  UpdateAssetCatalogDto,
} from '@peritasai/types';
import { RealtimeEventType } from '@peritasai/types';
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useRealtimeAssetUpdates } from '../asset/realtime-hooks';
import { assetCatalogRepository } from './repository';
import type { AssetCatalogItemEntity } from './asset-catalog.entity';

export const assetCatalogKeys = {
  all: ['asset-catalog'] as const,
  lists: () => [...assetCatalogKeys.all, 'list'] as const,
  list: (filters: AssetCatalogFilterParams) =>
    [...assetCatalogKeys.lists(), { filters }] as const,
  item: (id: string) => [...assetCatalogKeys.all, 'item', id] as const,
} as const;

export function useAssetCatalog(
  filters: AssetCatalogFilterParams = {},
  { enabled }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: assetCatalogKeys.list(filters),
    queryFn: () => assetCatalogRepository.getAssetCatalogPaginated(filters),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    enabled,
  });
}

export function useCreateAssetCatalogItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateAssetCatalogDto) =>
      assetCatalogRepository.createAssetCatalogItem(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assetCatalogKeys.lists() });
    },
  });
}

export function useUpdateAssetCatalogItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateAssetCatalogDto }) =>
      assetCatalogRepository.updateAssetCatalogItem(id, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assetCatalogKeys.lists() });
    },
  });
}

export function useAssetCatalogItem(id: string) {
  return useQuery({
    queryKey: assetCatalogKeys.item(id),
    queryFn: () => assetCatalogRepository.getAssetCatalogItem(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
  });
}

/**
 * Fetches multiple catalog items by id in parallel (there is no batch-by-ids
 * endpoint). Each item shares its cache entry with `useAssetCatalogItem`.
 * Also subscribes to realtime asset status/location updates and invalidates
 * the matching catalog item's cache entry, so availability (e.g. the Build
 * Cart "Available"/"Not available" pin) reflects changes live instead of
 * waiting out the 5-minute staleTime.
 */
export function useAssetCatalogItems(ids: string[]) {
  const queryClient = useQueryClient();

  useRealtimeAssetUpdates({
    eventTypes: [
      RealtimeEventType.ASSET_STATUS_UPDATED,
      RealtimeEventType.ASSET_LOCATION_UPDATED,
    ],
    onEvent: (event) => {
      if (
        event.type !== RealtimeEventType.ASSET_STATUS_UPDATED &&
        event.type !== RealtimeEventType.ASSET_LOCATION_UPDATED
      ) {
        return;
      }
      const { assetCatalogId } = (
        event as AssetStatusUpdatedEvent | AssetLocationUpdatedEvent
      ).data.asset;
      void queryClient.invalidateQueries({
        queryKey: assetCatalogKeys.item(assetCatalogId),
      });
    },
  });

  return useQueries({
    queries: ids.map((id) => ({
      queryKey: assetCatalogKeys.item(id),
      queryFn: (): Promise<AssetCatalogItemEntity> =>
        assetCatalogRepository.getAssetCatalogItem(id),
      staleTime: 5 * 60 * 1000,
      enabled: !!id,
    })),
  });
}
